import type { LayoutRectangle } from 'react-native';

/** Gap between the trigger edge and the menu, in px. */
export const DROPDOWN_SIDE_OFFSET = 4;

/** Minimum inset kept between the menu and the screen edges, in px. */
export const DROPDOWN_SCREEN_INSET = 8;

export type DropdownAlign = 'start' | 'center' | 'end';
export type DropdownSide = 'top' | 'bottom';

interface Size {
  width: number;
  height: number;
}

interface ComputeDropdownPositionOptions {
  align: DropdownAlign;
  side: DropdownSide;
  screenWidth: number;
  /**
   * The measured size of the menu content. Optional and only consulted for the
   * `align="center"` (horizontal) and `side="top"` (vertical) cases, which
   * fundamentally need a size. The common `align="end" | "start"`, `side="bottom"`
   * cases are anchored to screen edges and are correct before any measurement —
   * this is what keeps the menu visible on native, where the content view's
   * `onLayout` does not reliably fire inside a React Native `Modal`.
   */
  contentSize?: Size;
  /**
   * Extra pixels added to `top` to reconcile the trigger's `measureInWindow`
   * coordinates with the menu `Modal`'s content origin. On Android a
   * `statusBarTranslucent` Modal draws from the physical screen top while
   * `measureInWindow` reports below the status bar, so the menu lands ~one
   * status-bar too high; pass `StatusBar.currentHeight` here to correct it.
   * Defaults to 0 (iOS/web, where the origins already agree).
   */
  topOffset?: number;
}

/**
 * Absolute-position result for a dropdown menu. Exactly one of `left` / `right`
 * is set: edge-anchoring via `right` (for `align="end"`) avoids depending on the
 * menu's own width, so the menu can be positioned and shown on its first frame.
 */
export interface DropdownPosition {
  top: number;
  left?: number;
  right?: number;
}

/**
 * Compute the absolute position for a dropdown menu from its trigger's
 * window rectangle (via `measureInWindow`). Never gates on measurement for the
 * combinations the app actually uses (`align="end"`, `side="bottom"`), so the
 * menu renders visibly and anchored on native as well as web.
 */
export function computeDropdownPosition(
  trigger: LayoutRectangle,
  { align, side, screenWidth, contentSize, topOffset = 0 }: ComputeDropdownPositionOptions
): DropdownPosition {
  const { x, y, width, height } = trigger;
  const contentHeight = contentSize?.height ?? 0;
  const contentWidth = contentSize?.width ?? 0;

  const top =
    (side === 'top'
      ? y - contentHeight - DROPDOWN_SIDE_OFFSET
      : y + height + DROPDOWN_SIDE_OFFSET) + topOffset;

  if (align === 'start') {
    return { top, left: Math.max(DROPDOWN_SCREEN_INSET, x) };
  }

  if (align === 'end') {
    const right = screenWidth - (x + width);
    return { top, right: Math.max(DROPDOWN_SCREEN_INSET, right) };
  }

  // center — the only case that needs a measured width; unused today.
  const left = x + width / 2 - contentWidth / 2;
  return {
    top,
    left: Math.max(DROPDOWN_SCREEN_INSET, Math.min(left, screenWidth - contentWidth - DROPDOWN_SCREEN_INSET)),
  };
}
